import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/config';

const PermissionsContext = createContext();

export const usePermissionsContext = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissionsContext debe usarse dentro de PermissionsProvider');
  return ctx;
};

export const PermissionsProvider = ({ currentUser, children }) => {
  const [permissions, setPermissions] = useState(new Set());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    if (!currentUser) {
      setPermissions(new Set());
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get('/roles/my-permissions');
      setPermissions(new Set(data.permissions || []));
      setIsSuperAdmin(data.isSuperAdmin || false);
    } catch (err) {
      console.error('Error cargando permisos:', err);
      setPermissions(new Set());
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Cargar permisos al montar o cuando cambia el usuario
  useEffect(() => { loadPermissions(); }, [loadPermissions]);

  // Recargar cada 2 minutos para reflejar cambios de permisos en tiempo casi-real
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(loadPermissions, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, loadPermissions]);

  // can(permiso) → boolean
  const can = useCallback(
    (permiso) => isSuperAdmin || permissions.has(permiso),
    [permissions, isSuperAdmin]
  );

  // canAny(...permisos) → true si tiene al menos uno
  const canAny = useCallback(
    (...permisos) => isSuperAdmin || permisos.some(p => permissions.has(p)),
    [permissions, isSuperAdmin]
  );

  return (
    <PermissionsContext.Provider value={{ can, canAny, permissions, isSuperAdmin, loading, reload: loadPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};
